import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ErrorCode } from '@kcm/contracts'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { pathForStep, stepForPath } from '../components/FunnelLayout'

/**
 * Puts a returning user back where they left off (PLAN.md §11.3).
 *
 * The server owns the step; this only reconciles the URL with it. Two
 * directions, both worth having:
 *
 *   - **Forward.** Someone who verified their email yesterday and reopens the
 *     link should not retype it. The legacy funnel restarted them, which is a
 *     large share of the abandonment in §1.1.
 *   - **Back.** A deep link to `/compliance` without the session behind it
 *     cannot work — the API will refuse every call on that screen — so the user
 *     is better served landing on the step that can actually proceed.
 *
 * Runs once per page load. Continuously enforcing it would fight the user's own
 * Back button, and re-running on every navigation would undo the forward
 * transitions the steps themselves perform.
 */
export function useResume(): void {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const settled = useRef(false)

  const { data, isError, error } = useQuery({
    queryKey: ['session-state'],
    queryFn: ({ signal }) => endpoints.session(signal),
    // A missing session is the normal case for a first-time visitor, not a
    // failure worth retrying.
    retry: (_count, err) =>
      !(err instanceof ApiError && err.code === ErrorCode.SESSION_REQUIRED),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (settled.current) return

    // No session: step 1 is the only place they can be. Sending them there from
    // a deep link avoids a screen whose every request would 401.
    if (isError) {
      const missing = error instanceof ApiError && error.code === ErrorCode.SESSION_REQUIRED
      settled.current = true
      if (missing && stepForPath(pathname) !== 1) navigate('/', { replace: true })
      return
    }

    if (!data) return
    settled.current = true

    // The Didit return path and the post-KYC confirmation both resolve their
    // own state; bouncing them elsewhere would throw away the outcome or skip
    // the confirmation the user is in the middle of.
    if (pathname.startsWith('/verify') || pathname.startsWith('/confirm')) return

    const target = pathForStep(data.step)
    if (target !== pathname) navigate(target, { replace: true })
  }, [data, isError, error, navigate, pathname])
}

/**
 * "Continue where you left off", for the screens the server has just refused.
 *
 * They used to navigate to `/`, which is the email form — so the button that
 * promised to resume the application restarted it instead. The server owns the
 * step, so this asks it again rather than guessing from the error.
 */
export function useGoToCurrentStep(): () => void {
  const navigate = useNavigate()
  const qc = useQueryClient()

  return useCallback(() => {
    qc.fetchQuery({
      queryKey: ['session-state'],
      queryFn: ({ signal }) => endpoints.session(signal),
      // The cached copy is what sent the user to the refused screen.
      staleTime: 0,
    })
      .then((data) => navigate(pathForStep(data.step), { replace: true }))
      .catch(() => navigate('/', { replace: true }))
  }, [navigate, qc])
}
